import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useCan, usePolicy } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import {
  GiveNumberDialog,
  type BankNumber,
} from "@/routes/_authenticated/_front-desk/sarah/-components/give-number-dialog";
import { AddPhoneDialog } from "./-components/add-phone-dialog";
import { kindOf } from "./-components/phone-kind";
import { PhoneNumberRow } from "./-components/phone-number-row";

const TABS = [
  { value: "all", label: "All" },
  { value: "facility", label: "Facilities" },
  { value: "staff", label: "Staff" },
] as const;
type Tab = (typeof TABS)[number]["value"];
const isTab = (value: unknown): value is Tab => TABS.some((tab) => tab.value === value);

const EMPTY: Record<Tab, string> = {
  all: "No numbers yet. Ask us for one from Get help.",
  facility: "No facility has a number from the bank yet.",
  staff:
    "Nobody has a company number yet. Give one to your sales staff so leads call the company, not a personal phone.",
};

export const Route = createFileRoute("/_authenticated/_front-desk/sarah/numbers/")({
  validateSearch: (search: Record<string, unknown>): { tab?: Tab } => ({
    tab: isTab(search.tab) && search.tab !== "all" ? search.tab : undefined,
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "phone-number-bank"),
  component: PhoneList,
});

/** Every number the organization owns: the facilities' lines, the staff's company numbers, and the
 * ones waiting in the bank for whoever needs one next. */
function PhoneList() {
  const tab = Route.useSearch().tab ?? "all";
  const navigate = useNavigate({ from: Route.fullPath });
  const bank = useQuery(orpc.frontDesk.staff.numberBank.list.queryOptions());
  const canAssign = useCan("settings:manage");
  const canAdd = useCan("platform:manage");
  const canAsk = usePolicy().navigation.some((entry) => entry.id === "activation");
  const [adding, setAdding] = useState(false);
  const [giving, setGiving] = useState<BankNumber | null>(null);

  const rows = bank.data ?? [];
  const count = (kind: string) => rows.filter((row) => kindOf(row) === kind).length;
  const counts: Record<Tab, number> = {
    all: rows.length,
    facility: count("facility"),
    staff: count("staff"),
  };
  const shown = tab === "all" ? rows : rows.filter((row) => kindOf(row) === tab);
  const available = count("available");

  return (
    <div className="space-y-6">
      <PageHeader
        title="Phone"
        meta={
          bank.isPending
            ? "Every number the organization owns, and who holds each."
            : `${rows.length} ${rows.length === 1 ? "number" : "numbers"}, ${available} available in the bank`
        }
        actions={
          canAdd ? (
            <Button onClick={() => setAdding(true)}>
              <Plus className="size-4" />
              Add number
            </Button>
          ) : null
        }
      />

      <Tabs
        value={tab}
        onValueChange={(value) =>
          void navigate({ search: { tab: value === "all" ? undefined : (value as Tab) } })
        }
      >
        <TabsList>
          {TABS.map((entry) => (
            <TabsTrigger key={entry.value} value={entry.value}>
              {entry.label}
              <span className="text-muted-foreground ml-1.5 tabular-nums">
                {bank.isPending ? "" : counts[entry.value]}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {bank.isPending ? (
        <div className="space-y-3">
          {[0, 1, 2].map((key) => (
            <Skeleton key={key} className="h-16 w-full rounded-xl" />
          ))}
        </div>
      ) : bank.isError ? (
        <ReadFailed
          message="Couldn't load the numbers."
          error={bank.error}
          onRetry={() => void bank.refetch()}
          retrying={bank.isFetching}
        />
      ) : shown.length === 0 ? (
        <EmptyState
          title="No numbers yet"
          description={
            canAdd && tab === "all" ? "Add a number from our Twilio account." : EMPTY[tab]
          }
          action={
            !canAdd && tab === "all" && canAsk ? (
              <Button variant="outline" size="sm" asChild>
                <Link to="/settings/activation">Open activation requests</Link>
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card className="py-0">
          <ul className="divide-y">
            {shown.map((row) => (
              <PhoneNumberRow
                key={row.id}
                number={row}
                canAssign={canAssign}
                onAssign={() => setGiving(row)}
              />
            ))}
          </ul>
        </Card>
      )}

      <AddPhoneDialog
        key={tab}
        open={adding}
        onOpenChange={setAdding}
        defaultFor={tab === "facility" ? "facility" : "person"}
      />
      {giving ? (
        <GiveNumberDialog
          key={giving.id}
          number={giving}
          onOpenChange={(open) => !open && setGiving(null)}
        />
      ) : null}
    </div>
  );
}
